/**
 * English (en) translations for the Customer Identity admin UI.
 *
 * Keys are namespaced (e.g. `users.email`, `errors.user_email_already_exists`).
 * Placeholders use `{name}` syntax — e.g. `'Hello {name}'` filled by params.
 *
 * To add another language, copy this file (e.g. `es.js`), translate values,
 * register it in `./index.js`. All keys not present in a target language fall
 * back to English automatically.
 */
const en = {
  chrome: {
    siteScopeLabel: 'Storefront',
    siteScopeAll: 'All storefronts',
    siteScopeHint:
      'Filters Customers, Users, and User groups (not Sites). Stored in the URL.',
  },

  common: {
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    confirm: 'Confirm',
    back: 'Back',
    loading: 'Loading…',
    noResults: 'No results.',
    actions: 'Actions',
    status: 'Status',
    yes: 'Yes',
    no: 'No',
    requiredField: 'This field is required.',
    tryAgain: 'Please try again.',
    resizeDrawer: 'Resize drawer',
  },

  sites: {
    pageTitle: 'Sites',
    chromeSubtitle: 'Manage storefronts and their public URLs.',
    detailSubtitle: 'Storefront settings',
    newSite: 'New site',
  },

  platformUsers: {
    pageTitle: 'Platform users',
    chromeSubtitle: 'Internal staff who administer Extreme B2B.',
    newUser: 'New platform user',
  },

  directoryUsers: {
    pageTitle: 'Users',
    chromeSubtitle:
      'Login users across your storefronts. Scope by site in the header, then narrow by customer or search.',
    scopeHint:
      'Loads automatically for the current storefront scope (or all storefronts you can access). Use filters when you want to narrow the list.',
    filterCustomerLabel: 'Customer account',
    filterCustomerAll: 'All customers',
    pickSite:
      'Choose a site to load users. Logins are always scoped to a single storefront.',
    searchPlaceholder: 'Email, name, phone, customer or site…',
    couldNotLoad: 'Could not load users.',
    columns: {
      email: 'Email',
      name: 'Name',
      role: 'Role',
      status: 'Status',
      customer: 'Customer',
      site: 'Storefront',
      lastLogin: 'Last login',
    },
    openCustomer: 'Open customer',
    prev: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {pages} ({total} total)',
  },

  directoryUserGroups: {
    pageTitle: 'User groups',
    chromeSubtitle:
      'Groups are defined per site, then assigned to users. Purchase Program rules can target these groups.',
  },

  customers: {
    pageTitle: 'Customers',
    chromeSubtitle: 'Search and manage customer accounts.',
    tabs: {
      customers: 'Customers',
      userGroups: 'User groups',
    },
    tabHelpUserGroups:
      'Groups are defined per site, then assigned to users. Purchase Program rules can target these groups.',
    newCustomer: 'New customer',
    open: 'Open',
    created: 'Customer created.',
    deleted: 'Customer deleted.',
    couldNotLoad: 'Could not load customers.',
    columns: {
      name: 'Name',
      type: 'Type',
      email: 'Email',
      site: 'Site',
    },
    filters: {
      search: 'Search',
      searchPlaceholder: 'Name, email, ERP ID…',
      site: 'Site',
      allSites: 'All sites',
      searching: 'Searching…',
      searchAction: 'Search',
    },
    empty: 'No customers match the current filters.',
    selectSiteForGroups:
      'Choose a site in the filter above. User groups belong to one storefront site.',
    groupsScopedToFilter:
      'These groups apply only to the site currently selected in the filter.',
  },

  customer: {
    title: 'Customer',
    backToCustomers: 'Customers',
    editCustomer: 'Edit customer',
    deleteCustomer: 'Delete customer',
    deleteConfirmTitle: 'Delete customer',
    deleteConfirm:
      'Delete customer "{name}"? All users, addresses, and payment methods will also be removed.',
    requires2fa: '2FA required',
    customerUpdated: 'Customer updated.',
    couldNotLoadCustomer: 'Could not load customer.',
    saveCustomer: 'Save customer',
    nameOrCompany: 'Name / company',
    accountType: 'Account type',
    accountTypes: {
      individual: 'Individual',
      business: 'Business',
    },
    statuses: {
      active: 'Active',
      suspended: 'Suspended',
      closed: 'Closed',
    },
    email: 'Email',
    phone: 'Phone',
    erpCustomerId: 'ERP customer ID',
    externalCustomerId: 'External customer ID',
    require2fa: 'Require 2FA for all users of this customer',
    site: 'Site',
    selectSite: '— Select a site —',

    logo: 'Customer logo',
    logoHelp:
      'Shown where this customer is branded (e.g. embedded experiences). PNG, JPG, GIF, WebP, or SVG. Max 2 MB.',
    logoNone: 'No logo uploaded.',
    uploadLogo: 'Upload logo',
    uploadingLogo: 'Uploading…',
    removeLogo: 'Remove logo',
    logoUploadFailed: 'Could not upload logo.',
    logoRemoveFailed: 'Could not remove logo.',

    tabs: {
      users: 'Users',
      addresses: 'Addresses',
      payments: 'Payment methods',
    },
  },

  users: {
    title: 'Users',
    addUser: 'Add user',
    editUser: 'Edit user',
    saveUser: 'Save user',
    individualNotice:
      'Individual customers can have only one user (the owner). Switch to a business account to invite additional users.',
    columns: {
      email: 'Email',
      name: 'Name',
      hireDate: 'Hire date',
      role: 'Role',
      groups: 'Groups',
      twoFa: '2FA',
      status: 'Status',
    },
    fields: {
      email: 'Email',
      initialPassword: 'Initial password',
      initialPasswordHint: 'Leave blank to send invite',
      firstName: 'First name',
      lastName: 'Last name',
      phone: 'Phone',
      hireDate: 'Hire date',
      hireDateHint:
        'Optional. Used for reporting and can align with purchase programs that reset allowances by hire anniversary.',
      role: 'Role',
      externalUserId: 'External user ID',
    },
    roles: {
      owner: 'Owner',
      admin: 'Admin',
      member: 'Member',
    },
    statuses: {
      active: 'Active',
      invited: 'Invited',
      suspended: 'Suspended',
      closed: 'Closed',
    },
    twoFa: {
      enabled: 'Enabled',
      off: 'Off',
      summaryNone: 'None',
      summaryOrphan:
        '2FA on — no channel flags (stale or stuck; use Reset)',
      summaryOrphanHint:
        'The account is marked as having 2FA, but no authenticator, email, or SMS flags are set. This can happen after partial setup or data drift. Use “Reset all 2FA methods” to clear it.',
      sectionTitle: 'Two-factor authentication',
      sectionHint:
        'Enable or disable email and SMS codes for this user. Use reset if they are locked out or need to set up their authenticator again.',
      app: 'Authenticator',
      emailChannel: 'Email codes',
      smsChannel: 'SMS codes',
      on: 'On',
      resetAll: 'Reset all 2FA methods',
      resetConfirmTitle: 'Reset all 2FA?',
      resetConfirm:
        'This removes authenticator (TOTP), email OTP, and SMS OTP for {email}. Active sessions are ended. They can sign in with their password and set up 2FA again.',
      resetDone: 'All 2FA methods have been cleared for this user.',
      channelsUpdated: '2FA channel settings updated.',
    },
    actions: {
      edit: 'Edit',
      resetPassword: 'Reset password',
      delete: 'Delete',
    },
    empty: 'No users yet.',
    saved: 'User saved.',
    removed: 'User removed.',
    deleteConfirmTitle: 'Delete user',
    deleteConfirm: 'Remove user "{email}"? This cannot be undone.',
    resetPasswordTitle: 'Reset password',
    resetPasswordIntro:
      'Set a new password for this user. They will be required to use it on their next login.',
    newPasswordPlaceholder: 'New password (min 8 chars)',
    setPassword: 'Set password',
    passwordUpdated: 'Password updated.',
    couldNotLoad: 'Could not load users.',
    groupsHint:
      'Assign site-scoped groups (same keys as Purchase Program user-group assignments). Manage groups under Customers → User groups or Site detail.',
    groupsNone: '—',
    groupsManageOnSite: 'Create groups under Sites → your site → User groups.',
  },

  userGroups: {
    title: 'User groups',
    subtitle: 'Organize users into groups for this site (permissions and workflows).',
    groupKey: 'Group key',
    groupKeyHelp:
      'Use this exact key in Purchase Program assignments (entity type user group). Unique per account.',
    name: 'Group name',
    description: 'Description',
    members: 'Members',
    manageMembers: 'Manage members',
    editMembers: 'Members',
    membersPickerHelp:
      'Only storefront users on this site appear here. Membership is stored per group.',
    saveMembers: 'Save members',
    noUsersOnSite: 'No storefront users found for customers on this site.',
    addGroup: 'Add group',
    editGroup: 'Edit group',
    saveGroup: 'Save group',
    empty: 'No groups yet. Create a group to assign users from the customer Users tab.',
    deleteConfirmTitle: 'Delete group',
    deleteConfirm:
      'Delete group "{name}"? Users lose this membership; users themselves are not deleted.',
    created: 'Group created.',
    updated: 'Group updated.',
    removed: 'Group deleted.',
    couldNotLoad: 'Could not load groups.',
  },

  addresses: {
    title: 'Addresses',
    addAddress: 'Add address',
    editAddress: 'Edit address',
    newAddress: 'New address',
    saveAddress: 'Save address',
    columns: {
      label: 'Label',
      address: 'Address',
      city: 'City',
      country: 'Country',
      defaults: 'Defaults',
    },
    fields: {
      label: 'Label',
      contactName: 'Contact name',
      company: 'Company',
      phone: 'Phone',
      externalShiptoId: 'External ship-to ID',
      addressLine1: 'Address line 1',
      addressLine2: 'Address line 2',
      city: 'City',
      stateProvince: 'State/Province',
      postalCode: 'Postal code',
      country: 'Country',
      defaultShipping: 'Default shipping',
      defaultBilling: 'Default billing',
    },
    badges: {
      ship: 'Ship',
      bill: 'Bill',
    },
    saved: 'Address saved.',
    removed: 'Address removed.',
    deleteConfirmTitle: 'Remove address',
    deleteConfirm: 'Remove this address?',
    empty: 'No addresses yet.',
    couldNotLoad: 'Could not load addresses.',
  },

  payments: {
    title: 'Payment methods',
    securityNote: 'Tokenized via gateway. Card data is never stored.',
    columns: {
      brandType: 'Brand / type',
      lastFour: 'Last 4',
      expiry: 'Expiry',
      gateway: 'Gateway',
    },
    actions: {
      makeDefault: 'Make default',
      activate: 'Activate',
      deactivate: 'Deactivate',
    },
    badges: {
      default: 'Default',
      active: 'Active',
      inactive: 'Inactive',
    },
    deleteConfirmTitle: 'Remove payment method',
    deleteConfirm: 'Remove this saved payment method?',
    removed: 'Payment method removed.',
    empty: 'No saved payment methods.',
    couldNotLoad: 'Could not load payment methods.',
    updateFailed: 'Update failed.',
  },

  settings: {
    pageTitle: 'Settings',
    chromeSubtitle:
      'Define and manage configurable settings for your storefronts, customers, users, and addresses.',
    tabs: {
      definitions: 'Setting definitions',
      values: 'Manage values',
    },
    definitions: {
      addDefinition: 'New setting',
      editDefinition: 'Edit setting',
      empty: 'No settings defined yet. Create one to get started.',
      filtersGroup: 'Group',
      filtersAllGroups: 'All groups',
      filtersScope: 'Allowed scope',
      filtersAllScopes: 'Any scope',
      filtersSearch: 'Search by key, name, group…',
      filtersIncludeInactive: 'Include inactive',
      saved: 'Setting saved.',
      removed: 'Setting deleted.',
      deleteConfirmTitle: 'Delete setting',
      deleteConfirm:
        'Delete setting "{name}"? All scoped values will be removed too. This cannot be undone.',
      sectionDeleted: 'Section deleted.',
      group: 'Group',
      ungrouped: 'Ungrouped',
    },
    fields: {
      settingKey: 'Setting key',
      settingKeyHelp:
        'Stable code used by your application (e.g. checkout.allow_po_box). Lowercase, dot/underscore-separated.',
      name: 'Display name',
      description: 'Description',
      group: 'Group / category',
      groupHelp: 'Used to group related settings together (e.g. Checkout, Notifications).',
      dataType: 'Data type',
      inputType: 'Input type',
      defaultValue: 'Default value',
      defaultValueHelp: 'Used when no value has been set at any scope.',
      options: 'Options',
      optionValue: 'Value',
      optionLabel: 'Label',
      addOption: 'Add option',
      allowedScopes: 'Where can this be set?',
      allowedScopesHelp:
        'Pick the scopes where an admin (or end user) can override this setting.',
      isRequired: 'Required',
      isSecret: 'Sensitive (mask in UI)',
      isActive: 'Active',
      validation: 'Validation',
      minValue: 'Minimum',
      maxValue: 'Maximum',
      minLength: 'Min length',
      maxLength: 'Max length',
      pattern: 'Regex pattern',
      placeholder: 'Placeholder',
      helpText: 'Help text',
      sortOrder: 'Sort order',
      preview: 'Live preview',
    },
    dataTypes: {
      string: 'Text',
      integer: 'Integer',
      decimal: 'Decimal',
      boolean: 'True/false',
      date: 'Date',
      datetime: 'Date & time',
      json: 'JSON',
    },
    inputTypes: {
      text: 'Single-line text',
      textarea: 'Multi-line text',
      number: 'Number',
      decimal: 'Decimal',
      switch: 'Toggle switch',
      checkbox: 'Checkbox',
      select: 'Dropdown',
      multiselect: 'Multi-select',
      radio: 'Radio buttons',
      date: 'Date picker',
      datetime: 'Date & time picker',
      time: 'Time picker',
      color: 'Color picker',
      password: 'Password (masked)',
      email: 'Email',
      url: 'URL',
      json: 'JSON editor',
    },
    scopes: {
      global: 'Global',
      site: 'Site',
      customer: 'Customer',
      user: 'User',
      address: 'Address',
    },
    scopeHints: {
      global: 'Applies everywhere unless overridden.',
      site: 'Override per storefront.',
      customer: 'Override per customer account.',
      user: 'Override per individual login user.',
      address: 'Override per shipping/billing address.',
    },
    embedded: {
      sectionTitle: 'Settings',
      createHint:
        'Set values now or skip — they will be applied automatically once you save.',
      willBeAppliedAfterSave: 'Will be saved with the form',
      unsaved: 'Unsaved',
    },
    values: {
      pickScope: 'Pick a scope',
      pickScopeHint:
        'Choose where you want to apply settings. More specific scopes override less specific ones at runtime (address > user > customer > site > global).',
      scopeLabel: 'Scope',
      targetSite: 'Storefront',
      targetCustomer: 'Customer',
      targetUser: 'User',
      targetAddress: 'Address',
      pickTargetSite: 'Select a storefront…',
      pickTargetCustomer: 'Select a customer…',
      pickTargetUser: 'Select a user…',
      pickTargetAddress: 'Select an address…',
      noDefinitionsForScope:
        'No active settings allow overrides at this scope. Edit a setting and add this scope to its allowed scopes.',
      saved: 'Settings saved.',
      saveAll: 'Save all changes',
      reset: 'Reset to inherited',
      resetConfirmTitle: 'Reset to inherited?',
      resetConfirm:
        'Remove the {scope} override and fall back to the next scope (or default value). This cannot be undone.',
      sourceBadge: {
        default: 'Default',
        global: 'Global',
        site: 'Site',
        customer: 'Customer',
        user: 'User',
        address: 'Address',
      },
      sourceHint: 'Effective value comes from {source}.',
      currentValue: 'Current value',
      inherited: 'Inherited',
      overridden: 'Overridden here',
      empty: 'Pick a scope and target above to view and edit settings.',
      groupAll: 'All groups',
      noChanges: 'No unsaved changes.',
      unsavedChanges: '{count} unsaved change{plural}.',
    },
  },

  editHistory: {
    title: 'Edit history',
    lastUpdated: 'Last updated',
    noHistory: 'No edit history found.',
    loading: 'Loading history…',
    couldNotLoad: 'Could not load edit history.',
    columns: {
      when: 'When',
      action: 'Action',
      by: 'By',
      changes: 'Changes',
    },
    show: 'Show history',
    hide: 'Hide history',
  },

  errors: {
    // Customer / user identity
    user_email_already_exists: 'A user with this email already exists for this site.',
    customer_not_found: 'Customer not found.',
    user_not_found: 'User not found.',
    individual_customer_can_have_only_one_user:
      'Individual customers can have only one user (the owner).',
    cannot_demote_last_owner:
      'Cannot demote the last owner. Promote another user to owner first.',
    cannot_close_last_owner: 'Cannot close the last owner of this customer.',
    cannot_delete_last_owner: 'Cannot delete the last owner of this customer.',
    customer_id_and_email_required: 'Customer and email are required.',
    invalid_role: 'Invalid role.',
    invalid_status: 'Invalid status.',
    password_too_short: 'Password must be at least 8 characters.',

    // Sites
    site_name_already_exists: 'A site with this name already exists.',
    site_name_required: 'Site name is required.',
    site_not_found: 'Site not found.',

    // Platform users
    platform_user_email_already_exists:
      'A platform user with this email already exists.',
    email_and_password_required: 'Email and password are required.',
    email_already_registered: 'This email is already registered.',
    platform_user_not_found: 'Platform user not found.',

    // Addresses
    default_shipping_address_already_set:
      'Another address is already the default shipping address.',
    default_billing_address_already_set:
      'Another address is already the default billing address.',
    user_default_shipping_already_set:
      'This user already has a default shipping address.',
    user_default_billing_already_set:
      'This user already has a default billing address.',
    address_not_found: 'Address not found.',

    // Payments
    default_payment_method_already_set:
      'Another payment method is already set as default.',
    user_default_payment_method_already_set:
      'This user already has a default payment method.',
    payment_method_not_found: 'Payment method not found.',

    // Platform-user scope / access enforcement
    site_access_denied:
      'You do not have access to this site. Ask a super admin to grant access.',
    customer_access_denied:
      'You do not have access to this customer. Ask a super admin to grant access.',
    platform_user_must_have_site_access:
      'Select at least one site for this platform user.',
    platform_user_must_have_customer_access:
      'Select at least one customer for this platform user.',
    all_scope_requires_super_admin:
      'Only super admins can hold the "all sites" scope. Lower the scope or change the role.',
    invalid_site_id: 'One or more selected sites do not belong to your account.',
    invalid_customer_id:
      'One or more selected customers do not belong to your account.',
    invalid_scope: 'Invalid access scope.',
    requires_role_super_admin: 'This action requires the super admin role.',
    requires_role_site_admin: 'This action requires a site admin or higher role.',
    requires_scope_all:
      'This action requires global (all-sites) access. Ask a super admin.',
    cannot_delete_self: 'You cannot delete your own platform user.',

    // Auth / login
    no_2fa_fields_provided: 'No 2FA fields were provided.',
    user_group_not_found: 'User group was not found.',
    user_group_key_required: 'Group key is required.',
    user_group_key_already_exists: 'This group key is already used for your account.',
    user_group_name_already_exists: 'A group with this name already exists for this site.',
    user_group_name_required: 'Group name is required.',
    user_not_in_site_for_group: 'That user does not belong to this site.',
    user_group_not_in_user_site: 'One or more groups do not belong to this user’s site.',
    invalid_credentials: 'Email or password is incorrect.',
    account_locked: 'This account is locked. Try again later.',
    customer_not_active: 'This customer account is not active.',

    // Settings
    setting_key_required: 'Setting key is required.',
    setting_name_required: 'Display name is required.',
    setting_key_already_exists: 'A setting with this key already exists.',
    setting_definition_not_found: 'Setting definition not found.',
    setting_value_not_found: 'Setting value not found.',
    setting_scope_not_allowed:
      'This scope is not allowed for the chosen setting. Edit the definition to enable it.',
    setting_scope_id_required: 'Choose a target for this scope.',
    setting_allowed_scopes_required: 'Pick at least one scope where this setting can be set.',
    invalid_setting_scope: 'Unknown scope.',
    invalid_setting_data_type: 'Invalid data type.',
    invalid_setting_input_type: 'Invalid input type.',

    // Generic DB / API fallbacks
    unique_constraint_violation:
      'This value already exists. Please choose a different one.',
    foreign_key_violation: 'Referenced record does not exist.',
    required_field_missing: 'A required field is missing.',
    check_constraint_violation: 'A value did not pass validation.',
    validation_error: 'Some fields are invalid: {details}',
    request_failed: 'Request failed. Please try again.',
    network_error: 'Cannot reach the server. Check your connection and try again.',
  },
};

export default en;
